import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch, ApiError, clearDemoProvisionKey, clearDemoToken, getOrCreateDemoProvisionKey, getToken, isDemoSession, setDemoToken } from "./api";
import elumeLogo from "./assets/ELogo2.png";
import DemoDashboardPreview from "./Components/DemoDashboardPreview";
import { useUiLanguage } from "./i18n/UiLanguageContext";

const featureKeys = [
  ["whiteboard", "✦"],
  ["live", "✓"],
  ["insights", "↗"],
  ["planning", "◌"],
] as const;

function demoUrl(ticket: string) {
  return `${window.location.origin}${window.location.pathname}#/demo/account?ticket=${encodeURIComponent(ticket)}`;
}

function retryDuration(seconds: number, language: "en" | "ga") {
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 2) return language === "ga" ? "nóiméad amháin" : "one minute";
  return language === "ga" ? `${minutes} nóiméad` : `${minutes} minutes`;
}

export default function DemoAccountEntryPage() {
  const { t, language } = useUiLanguage();
  const location = useLocation();
  const ticket = useMemo(() => new URLSearchParams(location.search).get("ticket") || "", [location.search]);
  const [busy, setBusy] = useState(Boolean(ticket));
  const [checkingExistingDemo, setCheckingExistingDemo] = useState(() => !ticket && isDemoSession());
  const [canResumeDemo, setCanResumeDemo] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [popupUrl, setPopupUrl] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [takingLonger, setTakingLonger] = useState(false);
  const provisioningRef = useRef(false);

  useEffect(() => {
    if (!ticket) return;
    let cancelled = false;
    void apiFetch("/auth/public-demo-session/exchange", {
      method: "POST",
      headers: { "Idempotency-Key": getOrCreateDemoProvisionKey() },
      body: { ticket },
    })
      .then((data) => {
        if (cancelled) return;
        if (!data?.access_token) throw new Error("No demo session was returned.");
        setDemoToken(data.access_token);
        clearDemoProvisionKey();
        window.location.hash = "#/";
        window.location.reload();
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "This demo link is no longer available. Start a fresh demo.");
          setBusy(false);
        }
      });
    return () => { cancelled = true; };
  }, [ticket]);

  useEffect(() => {
    if (ticket || !isDemoSession()) {
      setCheckingExistingDemo(false);
      return;
    }
    let cancelled = false;
    setCheckingExistingDemo(true);
    void apiFetch("/auth/me")
      .then((account) => {
        if (!cancelled) setCanResumeDemo(account?.is_demo_account === true);
        if (!cancelled && account?.is_demo_account !== true) clearDemoToken();
      })
      .catch(() => {
        // A demo token must never fall back to the real-account token. Clear
        // only the tab token and leave any existing signed-in account intact.
        clearDemoToken();
      })
      .finally(() => { if (!cancelled) setCheckingExistingDemo(false); });
    return () => { cancelled = true; };
  }, [ticket]);

  useEffect(() => {
    if (!retryAfterSeconds || retryAfterSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setRetryAfterSeconds((remaining) => remaining && remaining > 1 ? remaining - 1 : null);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [retryAfterSeconds]);

  function continueDemo() {
    if (busy || checkingExistingDemo || !canResumeDemo) return;
    setBusy(true);
    window.location.hash = "#/";
    window.location.reload();
  }

  async function startDemo() {
    if (busy || checkingExistingDemo || retryAfterSeconds || provisioningRef.current) return;
    provisioningRef.current = true;
    setBusy(true); setError(null); setPopupUrl(null); setTakingLonger(false);
    const controller = new AbortController();
    const slowTimer = window.setTimeout(() => setTakingLonger(true), 8000);
    // Do not leave a visitor indefinitely disabled behind a failed upstream.
    // The server-held idempotency key makes a later manual retry safe even if
    // its first request committed after the browser gave up waiting.
    const abortTimer = window.setTimeout(() => controller.abort(), 45000);
    try {
      const data = await apiFetch("/auth/public-demo-session", {
        method: "POST",
        headers: { "Idempotency-Key": getOrCreateDemoProvisionKey() },
        signal: controller.signal,
      });
      if (!data?.ticket) throw new Error("No demo link was returned.");
      const url = demoUrl(data.ticket);
      // Preserve a signed-in teacher account by opening the scoped demo in its
      // own tab. Anonymous visitors continue in this tab.
      // A previous demo token is tab-scoped and can safely be replaced in this
      // tab. Only a real stored account login needs a separate demo tab.
      if (getToken() && !isDemoSession()) {
        const popup = window.open(url, "_blank", "noopener,noreferrer");
        if (!popup) setPopupUrl(url);
        setBusy(false);
        provisioningRef.current = false;
      } else {
        window.location.href = url;
      }
    } catch (err: any) {
      const retryAfter = err instanceof ApiError ? err.retryAfterSeconds : null;
      if (retryAfter) setRetryAfterSeconds(retryAfter);
      else if ([502, 503, 504].includes(Number(err?.status))) setError(t("demo.entryTemporary"));
      else if (err?.name === "AbortError") setError(t("demo.entryTemporary"));
      else setError(err?.message || t("demo.entryTemporary"));
      setBusy(false);
      provisioningRef.current = false;
    } finally {
      window.clearTimeout(slowTimer);
      window.clearTimeout(abortTimer);
    }
  }

  return <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_10%_8%,rgba(196,181,253,0.32),transparent_28%),radial-gradient(circle_at_92%_16%,rgba(94,234,212,0.25),transparent_25%),linear-gradient(145deg,#f8fafc,#ffffff_48%,#ecfdf5)] text-slate-900">
    <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:py-10">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3"><img src={elumeLogo} alt="Elume" className="h-11 w-11 object-contain sm:h-12 sm:w-12" /><div><div className="text-lg font-black tracking-tight text-violet-800">Elume</div><div className="text-xs font-semibold text-slate-600">Learn, Grow, Succeed</div></div></div>
        <a href="#/register" className="rounded-xl px-3 py-2 text-sm font-bold text-violet-800 underline decoration-violet-300 underline-offset-4 hover:text-violet-950">{t("demo.trialShort")}</a>
      </header>
      <section className="grid items-center gap-10 pb-10 pt-9 lg:grid-cols-[0.96fr_1.04fr] lg:py-16">
        <div className="max-w-xl">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">{t("demo.eyebrow")}</p>
          <h1 className="mt-4 text-4xl font-black leading-[0.98] tracking-tight text-slate-950 sm:text-6xl">{t("demo.headline")}</h1>
          <p className="mt-4 text-xl font-bold text-violet-800 sm:text-2xl">{t("demo.supporting")}</p>
          <p className="mt-5 max-w-lg text-base leading-7 text-slate-700 sm:text-lg">{t("demo.description")}</p>
          {error && <p role="alert" className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-800">{error}</p>}
          {retryAfterSeconds && <p role="status" aria-live="polite" className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">{t("demo.rateLimited", { time: retryDuration(retryAfterSeconds, language) })}</p>}
          {popupUrl && <a href={popupUrl} className="mt-5 inline-flex rounded-2xl bg-emerald-700 px-5 py-3 font-black text-white shadow-lg">{t("demo.openWorkspace")}</a>}
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {canResumeDemo ? <button type="button" onClick={continueDemo} disabled={busy || checkingExistingDemo} className="min-h-12 rounded-2xl bg-gradient-to-r from-violet-700 via-indigo-700 to-emerald-600 px-6 py-3 text-base font-black text-white shadow-[0_12px_28px_rgba(79,70,229,0.28)] transition hover:-translate-y-0.5 hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-200 disabled:cursor-wait disabled:opacity-70">{busy ? t("demo.opening") : t("demo.continue")}</button> : <button type="button" onClick={startDemo} disabled={busy || checkingExistingDemo || Boolean(retryAfterSeconds)} className="min-h-12 rounded-2xl bg-gradient-to-r from-violet-700 via-indigo-700 to-emerald-600 px-6 py-3 text-base font-black text-white shadow-[0_12px_28px_rgba(79,70,229,0.28)] transition hover:-translate-y-0.5 hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-200 disabled:cursor-wait disabled:opacity-70">{busy || checkingExistingDemo ? t("demo.opening") : error ? t("demo.tryAgain") : t("demo.explore")}</button>}
            <a href="#/register" className="inline-flex min-h-12 items-center justify-center rounded-2xl border-2 border-slate-200 bg-white px-6 py-3 text-base font-black text-slate-800 shadow-sm transition hover:border-violet-200 hover:bg-violet-50">{t("demo.trial")}</a>
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-600">{t("demo.reassurance")}</p>
          {takingLonger && busy && <p role="status" aria-live="polite" className="mt-3 text-sm font-semibold text-slate-700">{t("demo.entrySlow")}</p>}
          <button type="button" onClick={() => setShowVideo((open) => !open)} aria-expanded={showVideo} className="mt-6 inline-flex items-center gap-2 text-sm font-black text-violet-800 underline decoration-violet-300 underline-offset-4 hover:text-violet-950"><span className="grid h-6 w-6 place-items-center rounded-full bg-violet-700 text-xs text-white">▶</span>{t("demo.watch")}</button>
          {showVideo && <div className="mt-4 aspect-video overflow-hidden rounded-2xl border border-violet-100 bg-slate-950 shadow-lg"><iframe className="h-full w-full" src="https://www.youtube.com/embed/dNGSfgyOKYI?rel=0&modestbranding=1" title="Elume Advert" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>}
        </div>
        <div className="relative lg:pl-3"><div aria-hidden="true" className="absolute -inset-6 -z-10 rounded-full bg-gradient-to-br from-violet-200/60 via-transparent to-emerald-200/70 blur-2xl" /><DemoDashboardPreview /></div>
      </section>
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {featureKeys.map(([key, icon]) => <article key={key} className="rounded-3xl border border-white/90 bg-white/80 p-5 shadow-[0_10px_28px_rgba(15,23,42,0.07)] backdrop-blur"><div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-violet-600 to-emerald-600 text-lg font-black text-white">{icon}</div><h2 className="mt-4 font-black tracking-tight text-slate-900">{t(`demo.feature.${key}.title`)}</h2><p className="mt-2 text-sm leading-6 text-slate-600">{t(`demo.feature.${key}.body`)}</p></article>)}
      </section>
      <aside className="mt-8 rounded-3xl border border-teal-100 bg-white/85 px-5 py-4 text-sm leading-6 text-slate-700 shadow-sm">{t("demo.info")}</aside>
    </div>
  </main>;
}
